import React, { memo, useCallback, useEffect, useState } from 'react';

import ResetPasswordMessage from './components/ResetPasswordMessage';
import ResetPasswordRequest from './components/ResetPasswordRequest';

import { GeneralModel } from '../../../models';
import { GENERAL } from '../../../../constants';
import { sanitizePhoneNumber } from 'utils';
import { useLocation } from 'react-router';
import OTPInput from './components/OTPInput';

export interface IForgotPasswordProps {
  loadingStatus: GeneralModel.ILoadingStatus;
  resetPassword: (email: string, mobilePhoneNumber: string) => void;
  clearLoading: (key: string) => void;
  fetchCountryList: () => void;
  countryList: GeneralModel.INamedEntity[];
  navigate: (path: string) => void;
  validateResetPasswordToken: (token: string, email: string, mobilePhoneNumber: string) => void;
  IsValidateResetPasswordToken: boolean;
  validateLoadingStatus: GeneralModel.ILoadingStatus;
}

const ForgotPassword = ({
  loadingStatus,
  resetPassword,
  clearLoading,
  countryList,
  fetchCountryList,
  navigate,
  validateLoadingStatus,
  IsValidateResetPasswordToken,
  validateResetPasswordToken,
}: IForgotPasswordProps) => {
  const location = useLocation();
  const stateEmail = (location.state as { email: string })?.email || '';
  const statePhone = (location.state as { phone: string })?.phone || '';
  const [step, setStep] = useState(0);
  const [otp, setOTP] = useState('');
  const [mobilePhoneNumber, setMobilePhoneNumber] = useState('');
  const [selectedOption, setSelectedOption] = useState<string>(localStorage.getItem('preferredOption') || 'email');
  useEffect(() => {
    if (stateEmail) {
      setSelectedOption('email');
    }
    if (statePhone) {
      setSelectedOption('phone');
    }
  }, [stateEmail, statePhone]);

  const onResetPassword = useCallback(
    data => {
      if (selectedOption === 'email') {
        resetPassword(data.email, '');
      } else {
        const phone = sanitizePhoneNumber(data.email);
        setMobilePhoneNumber(phone);
        resetPassword('', phone);
      }
    },
    [selectedOption, setStep, navigate, resetPassword]
  );

  useEffect(() => {
    if (loadingStatus && !loadingStatus.isLoading && !loadingStatus.hasError && selectedOption === 'phone') {
      setStep(1);
    }
  }, [loadingStatus]);

  useEffect(() => {
    if (validateLoadingStatus && !validateLoadingStatus.isLoading && !validateLoadingStatus.hasError && IsValidateResetPasswordToken) {
      navigate(`/users/reset-password?token=${otp}&mobilePhoneNumber=${mobilePhoneNumber}`);
    }
  }, [otp, validateLoadingStatus, IsValidateResetPasswordToken, navigate]);

  useEffect(() => {
    return function unMount() {
      clearLoading(GENERAL.LOADING_KEY.RESET_PASSWORD);
      clearLoading(GENERAL.LOADING_KEY.VALIDATE_RESET_PASSWORD_TOKEN);
    };
  }, [clearLoading]);

  const onSubmitOTP = useCallback(
    data => {
      setOTP(data.otp);
      validateResetPasswordToken(data.otp, '', mobilePhoneNumber);
    },
    [mobilePhoneNumber, navigate, validateResetPasswordToken]
  );

  if (loadingStatus && !loadingStatus.isLoading && !loadingStatus.hasError && selectedOption === 'email') {
    return <ResetPasswordMessage selectedOption={selectedOption} />;
  }

  return (
    <>
      {step === 0 ? (
        <ResetPasswordRequest
          countryList={countryList}
          loadingStatus={loadingStatus}
          selectedOption={selectedOption}
          onResetPassword={onResetPassword}
          fetchCountryList={fetchCountryList}
          setSelectedOption={setSelectedOption}
          email={stateEmail}
          phone={statePhone}
        />
      ) : null}
      {step === 1 ? <OTPInput setStep={setStep} mobilePhoneNumber={mobilePhoneNumber} loadingStatus={validateLoadingStatus} onSubmitOTP={onSubmitOTP} /> : null}
    </>
  );
};

export default memo(ForgotPassword);
